import { useState, useRef, useEffect } from 'react';

const usePitchDetector = () => {
  const [pitch, setPitch] = useState(null);
  const [permissionError, setPermissionError] = useState(null);
  const [noteName, setNoteName] = useState(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const rafIdRef = useRef(null);

  // Convert frequency to note name (e.g., A4, C#5)
const getNoteName = (freq) =>  {
  const noteNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const A4 = 440;
  const semitone = 12 * (Math.log2(freq / A4));
  const noteIndex = Math.round(semitone) + 57; // A4 = MIDI 69
  const note = noteNames[noteIndex % 12];
  const octave = Math.floor(noteIndex / 12);
  return `${note}${octave}`;
}

// Autocorrelation function
const autoCorrelate = (buffer, sampleRate) => {
  const SIZE = buffer.length;
  let rms = 0;

  for (let i = 0; i < SIZE; i++) {
    const val = buffer[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return null; // too quiet

  let r1 = 0, r2 = SIZE - 1, thres = 0.2;
  for (let i = 0; i < SIZE / 2; i++) {
    if (Math.abs(buffer[i]) < thres) {
      r1 = i;
      break;
    }
  }
  for (let i = 1; i < SIZE / 2; i++) {
    if (Math.abs(buffer[SIZE - i]) < thres) {
      r2 = SIZE - i;
      break;
    }
  }

  buffer = buffer.slice(r1, r2);
  const newSize = buffer.length;
  const c = new Array(newSize).fill(0);

  for (let i = 0; i < newSize; i++) {
    for (let j = 0; j < newSize - i; j++) {
      c[i] += buffer[j] * buffer[j + i];
    }
  }

  let d = 0;
  while (c[d] > c[d + 1]) d++;
  let maxval = -1, maxpos = -1;
  for (let i = d; i < newSize; i++) {
    if (c[i] > maxval) {
      maxval = c[i];
      maxpos = i;
    }
  }

  let T0 = maxpos;
  return T0 ? sampleRate / T0 : null;
}

  useEffect(() => {
    let stream;

    const setup = async() =>  {
      try {
        
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
  
        const bufferLength = analyserRef.current.fftSize;
        dataArrayRef.current = new Float32Array(bufferLength);
  
        sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current.connect(analyserRef.current);
  
        detectPitch();
      } catch (error) {
        setPermissionError(error)
      }
    }

    setup();

    const detectPitch = () => {
      analyserRef.current.getFloatTimeDomainData(dataArrayRef.current);
      const frequency = autoCorrelate(dataArrayRef.current, audioContextRef.current.sampleRate);
      if (frequency) {
        const rounded = Math.round(frequency);
        setPitch(rounded);
        setNoteName(getNoteName(rounded));
      } else {
        setPitch(null);
        setNoteName(null);
      }

      rafIdRef.current = setTimeout(detectPitch, 500);
    }

    return () => {
      clearTimeout(rafIdRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return {
    pitch, 
    noteName,
    permissionError
  }

}

export default usePitchDetector;